import React, { useId, useRef } from 'react';
import Portal from './Portal';
import { IconButton } from './Button';
import { Icon } from '../icons';
import { useScrollLock, useOnEscape, useFocusTrap } from './overlayHooks';
import { useT } from '../../i18n';

/**
 * Drawer — side panel that slides in from an edge over a scrim.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   side: 'right' | 'left'           — default 'right'
 *   size: 'sm' | 'md' | 'lg'         — panel width (default 'md')
 *   title, description: ReactNode
 *   footer: ReactNode
 *   closeOnScrim: boolean            — default true
 *   children: body content
 */
const Drawer = ({
  open,
  onClose,
  side = 'right',
  size = 'md',
  title,
  description,
  footer,
  closeOnScrim = true,
  className = '',
  children,
}) => {
  const t = useT();
  const panelRef = useRef(null);
  const titleId = useId();
  const descId = useId();

  useScrollLock(open);
  useOnEscape(open, () => onClose?.());
  useFocusTrap(panelRef, open);

  if (!open) return null;

  return (
    <Portal>
      <div className="ps-scope ps-overlay ps-overlay--drawer" role="presentation">
        <div
          className="ps-overlay__scrim"
          onMouseDown={() => closeOnScrim && onClose?.()}
        />
        <div
          ref={panelRef}
          className={`ps-drawer ps-drawer--${side} ps-drawer--${size} ${className}`.trim()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={description ? descId : undefined}
          tabIndex={-1}
        >
          {title && (
            <header className="ps-drawer__header">
              <div className="ps-drawer__heading">
                <h2 className="ps-drawer__title" id={titleId}>{title}</h2>
                {description && (
                  <p className="ps-drawer__desc" id={descId}>{description}</p>
                )}
              </div>
              <IconButton
                aria-label={t('Close')}
                variant="ghost"
                size="icon-sm"
                onClick={() => onClose?.()}
              >
                <Icon.X />
              </IconButton>
            </header>
          )}
          <div className="ps-drawer__body">{children}</div>
          {footer && <footer className="ps-drawer__footer">{footer}</footer>}
        </div>
      </div>
    </Portal>
  );
};

export default Drawer;
